import * as React from 'react'
import { Task, TaskColumnConfig, TaskColumnProperty, TaskColumns, TaskProperty, ResourceRendererOptions, ResourceRendererResult, TaskRenderOptions, TaskRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import styled from 'styled-components'
import moment from '../../utilities/Moment'
import TaskHelper from '../../helpers/TaskHelper'
import Avatar from '../Avatar/Avatar'
import AvatarStack from '../Avatar/AvatarStack'
import TaskLabel from '../Tasks/TaskLabel'
import TaskIndicatorsContainer from '../Tasks/TaskIndicatorsContainer'
import TaskIndicators from '../Tasks/TaskIndicators'
import { Style } from '../../styles'
import Icon from '../Icons/Icon'
import TableRow from '../Tasks/TableRow'
import TableCellContent from '../Tasks/TableCellContent'
import TableCell from '../Tasks/TableCell'
import ResourceCheckbox from './ResourceCheckbox'
import TaskTitle from '../Tasks/TaskTitle'
import showdown from '../../utilities/showdown'
import TableSortButton from '../Tasks/TableSortButton'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

const TableCellTaskName = styled(TableCell)`
	width: 287.223px;
	min-width: 287.223px;
	max-width: 287.223px;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		width: 150px;
		min-width: 150px;
		max-width: 150px;
	}
`

const BoardIndicator = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 4px;
	overflow: hidden;
	padding-left: 2px;
	position: absolute;
	left: -6px;
	border-radius: 3px;
	z-index: 999;
	cursor: pointer;

	&:hover {
		overflow: visible;
		background-color: transparent !important;
	}
`

const BoardIndicatorContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	height: 100%;
	padding: 4px 8px;
	color: white;
	border-radius: 3px;
	margin-left: -2px;

	svg {
		width: 15px;
		margin-right: 4px;
	}
`

const CheckboxContainer = styled.div`
	margin-right: 8px;
`

export const DEFAULT_TASK_COLUMNS: TaskColumnProperty[] = [
  'name',
  'assignees',
  'list',
  'start_on',
  'due_on',
  'labels',
]

const TaskColumnConfigs: Record<TaskColumnProperty, TaskColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('Task::id') }),
    render: ({ task }) => task.id || '-'
  },
  name: {
    header: ({ onSortChange, sortValue }) => ({
      title: <>
        {i18n.t('Task::Task')}
        {onSortChange && <TableSortButton
          sortValue={sortValue}
          property='name'
          onClick={onSortChange}
        />}
      </>
    }),
    render: ({ task, onBoardClick, onCompletedChange }) => <>
      {task?.board && <BoardIndicator style={{ backgroundColor: task.board.color }} onClick={() => onBoardClick(task)}>
        <BoardIndicatorContent style={{ backgroundColor: task.board.color }}>
          <Icon icon='trello' />
          {task.board.name}
        </BoardIndicatorContent>
      </BoardIndicator>}

      <CheckboxContainer onClick={(e) => e.stopPropagation()}>
        <ResourceCheckbox
          checked={Boolean(task.completed)}
          disabled={false}
          onCheckedChange={() => onCompletedChange(task)}
        />
      </CheckboxContainer>

      <TaskTitle title={task.name}>
        {task.name}
      </TaskTitle>
    </>,
  },
  assignees: {
    header: ({ onSortChange, sortValue }) => ({
      title: <>
        {i18n.t('Task::assignees')}
        {onSortChange && <TableSortButton
          property='users.name'
          sortValue={sortValue}
          onClick={onSortChange}
        />}
      </>
    }),
    render: ({ task }) => {
      if (task?.assignees?.length === 0) return '-'
      return (
        <AvatarStack width={25.5}>
          {task.assignees.map(assignee => (
            <Avatar
              key={assignee.id}
              width={25.5}
              name={TaskHelper.getAssigneeName(assignee)}
              rounded
            />
          ))}
        </AvatarStack>
      )
    }
  },
  list: {
    header: ({ onSortChange, sortValue }) => ({
      title: <>
        {i18n.t('Task::list')}
        {onSortChange && <TableSortButton
          property='board_lists.name'
          sortValue={sortValue}
          onClick={onSortChange}
        />}
      </>
    }),
    render: ({ task }) => task?.list?.name || '-'
  },
  start_on: {
    header: ({ onSortChange, sortValue }) => ({
      title: <>
        {i18n.t('Task::start_on')}
        {onSortChange && <TableSortButton
          property='start_on'
          sortValue={sortValue}
          onClick={onSortChange}
        />}
      </>
    }),
    render: ({ task, setting }) => task.start_on ? moment(task.start_on).format(setting.date_format) : '-'
  },
  due_on: {
    header: ({ onSortChange, sortValue }) => ({
      title: <>
        {i18n.t('Task::due_on')}

        {onSortChange && <TableSortButton
          property='due_on'
          sortValue={sortValue}
          onClick={onSortChange}
        />}
      </>
    }),
    render: ({ task, setting }) => {
      if (!task.due_on) return '-'
      const momentDueOn = moment(task.due_on)
      const nowMoment = moment()
      const isOverdue = nowMoment.isAfter(momentDueOn, 'day')
      return (
        <>
          {isOverdue && (
            <SimpleTooltip content={i18n.t('TasksTable::This task is {{count}} days overdue', { count: nowMoment.diff(momentDueOn, 'day') })}>
              <span style={{ marginRight: 8, color: Style.color.brandDanger }}>
                <Icon icon='exclamation-triangle' />
              </span>
            </SimpleTooltip>
          )}
          {momentDueOn.format(setting.date_format)}
        </>
      )
    }
  },
  labels: {
    header: () => ({ title: i18n.t('Task::labels') }),
    render: ({ task }) => {
      if (!task?.labels?.length) return '-'

      return task?.labels?.map((label) => {
        return (
          <TaskLabel
            key={label.id}
            labelsExpanded={true}
            background={label.color}
          >
            <span>{label.name}</span>
          </TaskLabel>
        )
      })
    }
  },
  time_estimate: {
    header: () => ({ title: i18n.t('Task::time_estimate') }),
    render: ({ task }) => task?.time_estimate > 0
      ? moment.duration({ 'seconds': task.time_estimate }).format(`hh[${i18n.t("TimeFormatter::h")}]mm`, { trim: false })
      : '-'
  },
  contact: {
    header: () => ({ title: i18n.t('Task::contact') }),
    render: ({ task }) => task.contact?.name || '-'
  },
  project: {
    header: () => ({ title: i18n.t('Task::project') }),
    render: ({ task }) => task.project?.name || '-'
  },
  board: {
    header: () => ({ title: i18n.t('Task::board') }),
    render: ({ task }) => task.board?.name || '-'
  },
  created_at: {
    header: () => ({ title: i18n.t('Task::created_at') }),
    render: ({ task, setting }) => task.created_at ? moment(task.created_at).format(setting.date_format) : '-'
  },
  indicators: {
    header: () => ({ title: i18n.t('Task::indicators') }),
    render: ({ task }) => {
      return (
        <TaskIndicatorsContainer noWrap>
          <TaskIndicators task={task} />
        </TaskIndicatorsContainer>
      )
    }
  },
  description: {
    header: () => ({ title: i18n.t('Task::description') }),
    render: ({ task }) => task.description !== "" ? <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(task.description) }} /> : '-'
  },
  location: {
    header: () => ({ title: i18n.t('Task::location') }),
    render: ({ task }) => task.location || '-'
  },
  telephone_number: {
    header: () => ({ title: i18n.t('Task::telephone_number') }),
    render: ({ task }) => task.telephone_number ? <a onClick={(e) => e.stopPropagation()} href={`tel:${task.telephone_number}`}>{task.telephone_number}</a> : '-'
  }
}

export function getTaskTableRenderer(options: TaskRenderOptions): TaskRendererResult {
  const { onRowClick } = options
  const columns: { label: string, value: string }[] = TaskColumns.map(column => ({ label: i18n.t(`Task::${column}`), value: column }))
  const selectedColumns: TaskProperty[] = LocalStorage
    .get(LocalStorageKey.TASK_COLUMNS, DEFAULT_TASK_COLUMNS)
    .filter((column: TaskColumnProperty) => TaskColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...TaskColumnConfigs[column].header({
        onSortChange: options.onSortChange,
        sortValue: options.sortValue
      }),
      column: column,
      visible: true
    }))

  const renderHeader = () => {
    return (<>
      {headers.map((header, index) => {
        return (
          <TableCell key={index}>
            <TableCellContent>
              {header.title}
            </TableCellContent>
          </TableCell>
        )
      })
      }
    </>
    )
  }


  const renderRow = (task: Task, _index: number): React.ReactElement => {
    let cells: React.ReactNode[] = selectedColumns.map((column, index) => {
      if (index === 0) {
        return (
          <TableCellTaskName
            key={`${task.id}-${column}`}
            onClick={onRowClick ? () => onRowClick(task) : undefined}
          >
            <TableCellContent>
              {TaskColumnConfigs[column].render({ task, ...options })}
            </TableCellContent>
          </TableCellTaskName>
        )
      } else {
        return (
          <TableCell
            key={`${task.id}-${column}`}
            onClick={onRowClick ? () => onRowClick(task) : undefined}
          >
            <TableCellContent>
              {TaskColumnConfigs[column].render({ task, ...options })}
            </TableCellContent>
          </TableCell>
        )
      }
    })

    return (
      <TableRow key={task.id}>
        {cells}
      </TableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderHeader: renderHeader,
    renderRow: renderRow,
  }
}

export default getTaskTableRenderer
